const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const EXPECTED_WORKER_ORIGIN = 'https://fullykitted-contact-email.returnless-web.workers.dev';
const EXPECTED_ENDPOINT_PATH = '/api/contact-email';
const EXPECTED_ACTION = 'contact_email';
const TURNSTILE_TOKEN_LIMIT = 2048;
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;

export function isValidTurnstileToken(token) {
  return typeof token === 'string'
    && token.length > 0
    && token.length <= TURNSTILE_TOKEN_LIMIT
    && token === token.trim()
    && !/[\u0000-\u001f\u007f]/.test(token);
}

export function isSafeWorkerEndpoint(endpoint, pageOrigin) {
  try {
    const url = new URL(endpoint);
    return url.protocol === 'https:'
      && url.origin !== pageOrigin
      && url.origin === EXPECTED_WORKER_ORIGIN
      && url.pathname === EXPECTED_ENDPOINT_PATH
      && !url.search
      && !url.hash
      && !url.username
      && !url.password;
  } catch {
    return false;
  }
}

export function isSafeContactEmail(email) {
  return typeof email === 'string'
    && email.length <= 254
    && EMAIL_PATTERN.test(email);
}

function revealRoot(root) {
  const trigger = root.querySelector('[data-contact-email-trigger]');
  const widget = root.querySelector('[data-contact-email-widget]');
  const status = root.querySelector('[data-contact-email-status]');
  const error = root.querySelector('[data-contact-email-error]');
  const retry = root.querySelector('[data-contact-email-retry]');
  const sitekey = root.dataset.sitekey || '';
  const endpoint = root.dataset.endpoint || '';
  const pageOrigin = root.ownerDocument.defaultView.location.origin;

  if (!trigger || !widget || !status || !error || !retry || !sitekey || !isSafeWorkerEndpoint(endpoint, pageOrigin)) {
    root.hidden = true;
    return;
  }

  const win = root.ownerDocument.defaultView;
  const doc = root.ownerDocument;
  let scriptPromise = null;
  let widgetId = null;
  let submitting = false;
  let challengeActive = false;
  let completed = false;

  function setStatus(message) {
    status.replaceChildren(doc.createTextNode(message));
    status.hidden = false;
    error.hidden = true;
    error.textContent = '';
  }

  function setFailure() {
    if (completed) return;
    challengeActive = false;
    status.hidden = true;
    status.textContent = '';
    error.textContent = "We couldn't complete the check. Try again or send us a message using the form above.";
    error.hidden = false;
    widget.hidden = true;
    trigger.hidden = true;
    trigger.disabled = false;
    retry.hidden = false;
    retry.disabled = false;
  }

  function loadTurnstile() {
    if (typeof win.turnstile?.render === 'function') return Promise.resolve(win.turnstile);
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
      const script = doc.createElement('script');
      let settled = false;
      const timer = win.setTimeout(() => finish(new Error('unavailable')), 12_000);
      function finish(error) {
        if (settled) return;
        settled = true;
        win.clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        if (error) {
          script.remove();
          reject(error);
          return;
        }
        resolve(win.turnstile);
      }
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (typeof win.turnstile?.render === 'function') finish();
        else finish(new Error('unavailable'));
      };
      script.onerror = () => finish(new Error('unavailable'));
      doc.head.appendChild(script);
    }).catch((error) => {
      scriptPromise = null;
      throw error;
    });
    return scriptPromise;
  }

  function showWidget(turnstile) {
    if (completed) return;
    widget.hidden = false;
    trigger.hidden = true;
    retry.hidden = true;
    trigger.disabled = true;
    challengeActive = true;
    setStatus('Complete the security check to show the email.');
    if (widgetId !== null) {
      try {
        turnstile.reset(widgetId);
        return;
      } catch {
        widgetId = null;
        widget.replaceChildren();
      }
    }
    try {
      widgetId = turnstile.render(widget, {
        sitekey,
        action: EXPECTED_ACTION,
        theme: 'auto',
        callback: (token) => { if (challengeActive && !completed) void submitToken(token); },
        'expired-callback': () => { if (challengeActive && !completed) setFailure(); },
        'error-callback': () => { if (challengeActive && !completed) setFailure(); return true; },
        'timeout-callback': () => { if (challengeActive && !completed) setFailure(); }
      });
    } catch {
      setFailure();
    }
  }

  async function submitToken(token) {
    if (submitting || completed || !challengeActive) return;
    if (!isValidTurnstileToken(token)) {
      setFailure();
      return;
    }
    submitting = true;
    challengeActive = false;
    retry.disabled = true;
    setStatus('Checking the request…');
    const controller = new win.AbortController();
    const timer = win.setTimeout(() => controller.abort(), 8_000);
    try {
      const response = await win.fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!response.ok || !response.headers.get('Content-Type')?.toLowerCase().includes('application/json')) {
        throw new Error('unavailable');
      }
      const result = await response.json();
      if (!result || Object.keys(result).length !== 1 || !isSafeContactEmail(result.email)) {
        throw new Error('unavailable');
      }
      const link = doc.createElement('a');
      link.href = `mailto:${result.email}`;
      link.textContent = result.email;
      status.replaceChildren(doc.createTextNode('Email: '), link);
      completed = true;
      status.hidden = false;
      error.hidden = true;
      error.textContent = '';
      widget.hidden = true;
      trigger.hidden = true;
      retry.hidden = true;
    } catch {
      setFailure();
    } finally {
      win.clearTimeout(timer);
      submitting = false;
      retry.disabled = false;
    }
  }

  async function beginChallenge() {
    if (submitting || completed) return;
    retry.hidden = true;
    error.hidden = true;
    trigger.disabled = true;
    trigger.textContent = 'Loading the security check…';
    trigger.hidden = false;
    setStatus('Loading the security check…');
    try {
      const turnstile = await loadTurnstile();
      showWidget(turnstile);
    } catch {
      setFailure();
    }
  }

  trigger.addEventListener('click', () => { void beginChallenge(); });
  retry.addEventListener('click', () => {
    if (completed) return;
    error.hidden = true;
    if (widgetId !== null && typeof win.turnstile?.reset === 'function') {
      challengeActive = true;
      widget.hidden = false;
      retry.hidden = true;
      trigger.hidden = true;
      setStatus('Complete the security check to show the email.');
      try { win.turnstile.reset(widgetId); } catch { widgetId = null; void beginChallenge(); }
    } else {
      void beginChallenge();
    }
  });
}

const initializedRoots = new WeakSet();

export function initializeContactEmailReveal(root) {
  if (!root || initializedRoots.has(root)) return;
  initializedRoots.add(root);
  revealRoot(root);
}

function initialize() {
  for (const root of document.querySelectorAll('[data-contact-email-reveal]')) initializeContactEmailReveal(root);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
}
